/**
 * Change History Service
 *
 * Provides API operations for creating and fetching entity change history.
 * - createChangeHistoryAPI: Fire-and-forget mutation (no await) — writes to dim_change_history
 * - fetchChangeHistoryAPI: Query to list change history entries for an entity
 */
import { createChangeHistory } from 'src/graphql/mutations';
import { listChangeHistory } from 'src/graphql/queries';
import {
  ChangeEntityType,
  ChangeHistoryEntry,
  ChangeHistoryParams,
  ChangeHistoryResponse,
  ChangeOperationType,
  ChangeSectionName,
  FieldChange,
  SECTION_LABELS,
} from 'src/types/change-history/changeHistory.types';
import { logger } from 'src/utils/logger';
import { formatApiError } from 'src/utils/errorUtils';
import { fetchGraphQLRequest } from './clients/graphqlClient';

// ============================================================================
// Types
// ============================================================================

/** Input for creating a change history entry */
export interface CreateChangeHistoryInput {
  entity_type: string;
  rpn_id?: number | null;
  fpn_id?: number | null;
  product_line_id?: number | null;
  section_name: string;
  operation_type: string;
  created_by: string;
  field_changes: string; // JSON stringified FieldChange[]
  record_count?: number;
}

/** Raw entry from the GraphQL API */
interface RawChangeHistoryEntry {
  change_id: number;
  entity_type: string;
  rpn_id: number | null;
  fpn_id: number | null;
  product_line_id: number | null;
  section_name: string;
  operation_type: string;
  created_by: string;
  created_at: string;
  field_changes: string; // JSON string
  record_count: number | null;
}

// ============================================================================
// Create Change History (Fire-and-Forget)
// ============================================================================

/**
 * Creates a change history entry via GraphQL mutation.
 * Designed to be called WITHOUT await (fire-and-forget) to avoid adding latency.
 *
 * @param input - Change history entry data
 * @returns Promise that resolves when the mutation completes (callers should NOT await this)
 */
export const createChangeHistoryAPI = async (input: CreateChangeHistoryInput): Promise<void> => {
  try {
    logger.info('Creating change history entry', { input: { ...input, field_changes: '(omitted)' } });

    await fetchGraphQLRequest<{ createChangeHistory: { numberOfRecordsUpdated: number } }>({
      query: createChangeHistory,
      variables: { input },
      requestName: 'createChangeHistory',
      requestType: 'mutation',
    });

    logger.info('Change history entry created successfully', {
      entity_type: input.entity_type,
      section_name: input.section_name,
      record_count: input.record_count,
    });
  } catch (error) {
    // Silently log — this should never block the user's save operation
    logger.warn('Failed to create change history entry', {
      error: formatApiError('Failed to create change history', error),
      input: { ...input, field_changes: '(omitted)' },
    });
  }
};

// ============================================================================
// List Change History
// ============================================================================

/**
 * Converts a raw API entry into a typed ChangeHistoryEntry
 */
const convertRawEntry = (raw: RawChangeHistoryEntry): ChangeHistoryEntry => {
  // Parse field_changes JSON string
  let fieldChanges: FieldChange[] = [];
  try {
    const parsed = JSON.parse(raw.field_changes);
    if (Array.isArray(parsed)) {
      fieldChanges = parsed;
    }
  } catch {
    // field_changes was not valid JSON
  }

  const sectionLabel = SECTION_LABELS[raw.section_name as ChangeSectionName] || raw.section_name;

  return {
    change_id: `ch-${raw.change_id}`,
    created_at: raw.created_at,
    created_by: raw.created_by,
    entity_type: raw.entity_type as ChangeEntityType,
    entity_id: raw.rpn_id || raw.fpn_id || 0,
    entity_name: '',
    section: raw.section_name as ChangeSectionName,
    section_label: sectionLabel,
    operation: raw.operation_type as ChangeOperationType,
    summary: `${
      raw.operation_type === ChangeOperationType.CREATE
        ? 'Created'
        : raw.operation_type === ChangeOperationType.DELETE
          ? 'Deleted'
          : 'Updated'
    } ${sectionLabel}${raw.record_count && raw.record_count > 0 ? ` (${raw.record_count} field${raw.record_count > 1 ? 's' : ''})` : ''}`,
    field_changes: fieldChanges,
    record_count: raw.record_count ?? 0,
    is_bulk_operation: raw.entity_type === ChangeEntityType.BULK_EDIT,
  };
};

/**
 * Fetches change history for an entity from the dedicated dim_change_history table
 * via the listChangeHistory RDS Direct Resolver.
 *
 * @param params - Change history query parameters
 * @returns Promise resolving to ChangeHistoryResponse
 */
export const fetchChangeHistoryAPI = async (params: ChangeHistoryParams): Promise<ChangeHistoryResponse> => {
  try {
    logger.info('Fetching change history', { params });

    const input: Record<string, unknown> = {
      entity_type: params.entity_type,
    };

    // Set rpn_id or fpn_id based on entity type
    if (params.entity_type === ChangeEntityType.RPN) {
      input.rpn_id = params.entity_id;
      input.fpn_id = null;
    } else if (params.entity_type === ChangeEntityType.FPN) {
      input.rpn_id = null;
      input.fpn_id = params.entity_id;
    }

    if (params.product_line_id) {
      input.product_line_id = params.product_line_id;
    } else {
      input.product_line_id = null;
    }

    const result = await fetchGraphQLRequest<{ listChangeHistory: RawChangeHistoryEntry[] }>({
      query: listChangeHistory,
      variables: { input },
      requestName: 'listChangeHistory',
      requestType: 'query',
    });

    const rawEntries = result.listChangeHistory || [];

    // Convert raw entries to typed ChangeHistoryEntry objects
    const entries = rawEntries.map(convertRawEntry);

    // Sort by date descending (most recent first)
    entries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    logger.info('Change history fetched successfully', {
      totalEntries: entries.length,
    });

    return {
      entries,
      total_count: entries.length,
      has_more: false,
    };
  } catch (error) {
    logger.error('Error fetching change history:', { error, params });
    throw formatApiError('Failed to fetch change history', error);
  }
};
